/**
 * F61 (A2/A3) — a page's KPI tiles from `GET /admin/page-kpis`.
 *
 * One read on mount (not audited, served from a 15 s server cache), and a
 * `fresh` read after any admin action (`onAdminMutation`, debounced 500 ms —
 * the F60b nav-counts rule), so a suspend/resolve updates the tiles at once.
 * Best-effort: a failed read keeps the previous numbers (or `null`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminPageKpiPage, AdminPageKpis } from '@bravemove/types';
import { adminPageKpisApi } from '../api/pageKpis';
import { onAdminMutation } from '../auth/adminMutations';

const MUTATION_DEBOUNCE_MS = 500;

export function usePageKpis<P extends AdminPageKpiPage>(
  page: P,
  enabled = true,
): AdminPageKpis<P> | null {
  const [kpis, setKpis] = useState<AdminPageKpis<P> | null>(null);
  const alive = useRef(true);

  const load = useCallback(
    async (fresh: boolean) => {
      try {
        const next = await adminPageKpisApi.get(page, fresh ? { fresh: true } : {});
        if (alive.current) setKpis(next);
      } catch {
        // KPI tiles are best-effort — keep the last numbers.
      }
    },
    [page],
  );

  useEffect(() => {
    alive.current = true;
    if (!enabled) return undefined;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- load-on-mount; state lands after the await
    void load(false);
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = onAdminMutation(() => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => void load(true), MUTATION_DEBOUNCE_MS);
    });
    return () => {
      alive.current = false;
      if (timer !== null) clearTimeout(timer);
      unsubscribe();
    };
  }, [enabled, load]);

  return kpis;
}
